import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ReplayIcon from "@mui/icons-material/Replay";
import baseUrl from "../../../../../api/baseUrl";
import { getCurrencySymbol } from "../../../../../utils/currency/currencyUtils";
import SelectedBillPopUp from "../../../../../branch/pages/sales/invoice/SelectedBillPopUp";
import AdminAuthModal from "./AdminAuthModal";

type SalesReturnSearchModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const formatDateTime = (value: any) => {
  if (!value) {
    return "-";
  }

  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) {
    return "-";
  }

  return dt.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

function SalesReturnSearchModal({
  isOpen,
  onClose,
}: SalesReturnSearchModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [bills, setBills] = useState<any[]>([]);
  const [loadingBills, setLoadingBills] = useState(false);
  const [loadingBillDetails, setLoadingBillDetails] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedBill, setSelectedBill] = useState<any>(null);
  const [pendingBill, setPendingBill] = useState<any>(null);
  const [isAdminAuthOpen, setIsAdminAuthOpen] = useState(false);

  const currencySymbol = getCurrencySymbol();

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setBills([]);
      setErrorMessage("");
      return;
    }

    let isActive = true;

    const fetchBills = async () => {
      setLoadingBills(true);
      setErrorMessage("");

      try {
        const response = await baseUrl.get("/branch/pos/get-total-sales");
        const loadedBills = Array.isArray(response.data?.data)
          ? [...response.data.data]
          : [];

        loadedBills.sort((left: any, right: any) => {
          const leftTime = new Date(left.createdAt || 0).getTime();
          const rightTime = new Date(right.createdAt || 0).getTime();
          return rightTime - leftTime;
        });

        if (isActive) {
          setBills(loadedBills);
        }
      } catch (error) {
        console.error("Error loading POS bills:", error);
        if (isActive) {
          setBills([]);
          setErrorMessage("Unable to load sales bills. Please try again.");
        }
      } finally {
        if (isActive) {
          setLoadingBills(false);
        }
      }
    };

    fetchBills();

    return () => {
      isActive = false;
    };
  }, [isOpen]);

  const filteredBills = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return bills.slice(0, 25);
    }

    return bills
      .filter((bill: any) => {
        const billNumber = String(bill.billNumber || "").toLowerCase();
        const customerName = String(
          bill.customerName || bill.Customer?.name || bill.customer?.name || "",
        ).toLowerCase();
        return billNumber.includes(query) || customerName.includes(query);
      })
      .slice(0, 50);
  }, [bills, searchQuery]);

  const handleOpenBill = async (bill: any) => {
    setLoadingBillDetails(true);
    setErrorMessage("");

    try {
      const response = await baseUrl.get(
        `/branch/sale/sale-details/${bill.billNumber}`,
      );
      const saleDetails = response.data?.data || {};

      setSelectedBill({
        billNumber: bill.billNumber,
        ...saleDetails,
        payments: {
          ...(saleDetails.payments || {}),
          isCreditSale: Boolean(saleDetails.payments?.isCreditSale),
          unpaidAmount: Number(
            saleDetails.payments?.unpaidAmount ||
              saleDetails.payments?.creditAmount ||
              0,
          ),
          creditAmount: Number(
            saleDetails.payments?.creditAmount ||
              saleDetails.payments?.unpaidAmount ||
              0,
          ),
        },
      });
    } catch (error) {
      console.error("Error loading selected sale bill:", error);
      setErrorMessage("Unable to open this bill. Please try again.");
    } finally {
      setLoadingBillDetails(false);
    }
  };

  const requestOpenBill = (bill: any) => {
    setPendingBill(bill);
    setIsAdminAuthOpen(true);
  };

  const handleAdminVerified = () => {
    setIsAdminAuthOpen(false);
    if (pendingBill) {
      handleOpenBill(pendingBill);
    }
    setPendingBill(null);
  };

  const handleAdminAuthCancel = () => {
    setIsAdminAuthOpen(false);
    setPendingBill(null);
  };

  const handleCloseSelectedBill = () => {
    setSelectedBill(null);
  };

  const handleCloseLookup = () => {
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleCloseLookup} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          Sales Return Lookup
          <IconButton
            aria-label="close"
            onClick={handleCloseLookup}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Search a bill number, open it, then use the bill dialog to edit and
            save the return just like in the dashboard invoice flow.
          </Typography>

          <TextField
            fullWidth
            label="Bill Number"
            placeholder="Type bill number or customer name"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {loadingBills ? (
            <div
              style={{
                minHeight: 180,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={28} />
            </div>
          ) : (
            <>
              {errorMessage && (
                <Typography
                  sx={{
                    mb: 2,
                    color: "error.main",
                    fontWeight: 600,
                  }}
                >
                  {errorMessage}
                </Typography>
              )}

              <Table size="small" sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Bill No</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell align="right">Net Total</TableCell>
                    <TableCell align="right">Paid</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No bills found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBills.map((bill: any) => (
                      <TableRow key={bill.id} hover>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {bill.billNumber || "-"}
                        </TableCell>
                        <TableCell>{formatDateTime(bill.createdAt)}</TableCell>
                        <TableCell>
                          {bill.customerName ||
                            bill.Customer?.name ||
                            bill.customer?.name ||
                            (bill.customerId ? "Customer" : "Walk-In Customer")}
                        </TableCell>
                        <TableCell align="right">
                          {currencySymbol}{" "}
                          {Number(bill.netTotal || 0).toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          {currencySymbol}{" "}
                          {Number(bill.amountRecived || 0).toFixed(2)}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<ReplayIcon />}
                            onClick={() => requestOpenBill(bill)}
                            disabled={loadingBillDetails}
                            sx={{
                              opacity: "1 !important",
                              visibility: "visible !important",
                              backgroundColor: "#1565c0 !important",
                              color: "#fff !important",
                              boxShadow: "none !important",
                              "&:hover": {
                                backgroundColor: "#0d47a1 !important",
                              },
                              "&.Mui-disabled": {
                                backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                color: "rgba(0, 0, 0, 0.26) !important",
                              },
                            }}
                          >
                            Open Bill
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseLookup}>Close</Button>
        </DialogActions>
      </Dialog>

      <SelectedBillPopUp
        selectedBill={selectedBill}
        openModal={Boolean(selectedBill)}
        handleCloseModal={handleCloseSelectedBill}
      />

      <AdminAuthModal
        isOpen={isAdminAuthOpen}
        onCancel={handleAdminAuthCancel}
        onVerified={handleAdminVerified}
      />
    </>
  );
}

export default SalesReturnSearchModal;
